import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BadgeCheck, Clock, FileText, Flame, PauseCircle, Pencil, PlayCircle, Plus, ShieldCheck, Sparkles, Upload } from "lucide-react";
import { ServiceBoardDialog } from "@/components/marketplace/service-board-dialog";
import { ServicePromoteDialog } from "@/components/marketplace/service-promote-dialog";
import { isFeatured } from "@/lib/marketplace/promotions";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/sell")({
  head: () => ({
    meta: [
      { title: "Sell a service | BeHot.Now Marketplace" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SellPage,
});

const COUNTRIES = [
  "AE", "US", "GB", "DE", "FR", "IT", "ES", "PT", "BR", "RU", "NL", "SE", "NO", "DK", "CH", "AT", "BE", "IE",
  "PL", "CZ", "IN", "PK", "PH", "ID", "NG", "ZA", "EG", "SA", "QA", "KW", "TR", "UA", "CA", "AU", "NZ", "JP", "KR", "SG", "MX", "AR", "CO", "CL",
];

const STATUS_TONE: Record<string, "secondary" | "outline" | "destructive"> = {
  application_draft: "outline",
  kyc_pending: "secondary",
  kyc_more_info_required: "secondary",
  kyc_rejected: "destructive",
  seller_approved: "secondary",
  seller_suspended: "destructive",
};

function SellPage() {
  const { t } = useT();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const { data: application, isLoading } = useQuery({
    queryKey: ["mp-seller-application", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("marketplace_seller_applications")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  const { data: myServices } = useQuery({
    queryKey: ["mp-my-services", user?.id],
    enabled: Boolean(user?.id) && application?.application_status === "seller_approved",
    queryFn: async () => {
      const { data } = await supabase
        .from("marketplace_services")
        .select(
          "id, title, slug, status, short_description, cover_url, listing_id, featured_until, starting_price_cents, orders_count, rating_avg, rating_count, created_at",
        )
        .eq("seller_user_id", user!.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: myProfile } = useQuery({
    queryKey: ["mp-seller-profile", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("slug, handle")
        .eq("id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  const [boardService, setBoardService] = useState<
    | {
        id: string;
        title: string;
        slug: string;
        short_description: string | null;
        cover_url: string | null;
        listing_id: string | null;
      }
    | null
  >(null);

  const [featureService, setFeatureService] = useState<
    | {
        id: string;
        title: string;
        featured_until: string | null;
      }
    | null
  >(null);

  const setServiceStatus = useMutation({
    mutationFn: async ({ id, next }: { id: string; next: "paused" | "pending_moderation" }) => {
      const { error } = await supabase.from("marketplace_services").update({ status: next }).eq("id", id);
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: ["mp-my-services"] });
      toast.success(next === "paused" ? t("mpsell.sell.toast.pausedSuccess") : t("mpsell.sell.toast.moderationSuccess"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mpsell.sell.toast.updateError")),
  });


  const [sellerType, setSellerType] = useState<"individual" | "business">("individual");
  const [legalName, setLegalName] = useState("");
  const [country, setCountry] = useState("AE");
  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [tradeLicense, setTradeLicense] = useState("");
  const [tradeLicenseExpiry, setTradeLicenseExpiry] = useState("");
  const [licensingAuthority, setLicensingAuthority] = useState("");
  const [legalForm, setLegalForm] = useState("llc");
  const [incorporationDate, setIncorporationDate] = useState("");
  const [bizAddress, setBizAddress] = useState("");
  const [bizCity, setBizCity] = useState("");
  const [bizPostal, setBizPostal] = useState("");
  const [bizCountry, setBizCountry] = useState("AE");
  const [bizEmail, setBizEmail] = useState("");
  const [bizPhone, setBizPhone] = useState("");
  const [bizWebsite, setBizWebsite] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [taxId, setTaxId] = useState("");
  const [documentType, setDocumentType] = useState("passport");
  const [files, setFiles] = useState<File[]>([]);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Prefill from an existing application so the member can amend and resubmit.
  useEffect(() => {
    if (!application) return;
    setSellerType((application.seller_type as "individual" | "business") ?? "individual");
    setLegalName(application.legal_name ?? "");
    setCountry(application.country ?? "AE");
    setPhone(application.phone ?? "");
    setAddressLine1(application.address_line1 ?? "");
    setCity(application.address_city ?? "");
    setPostalCode(application.address_postal_code ?? "");
    setBusinessName(application.business_name ?? "");
    setRegNumber(application.business_registration_number ?? "");
    setTradeLicense(application.trade_license_number ?? "");
    setTradeLicenseExpiry(application.trade_license_expiry ?? "");
    setLicensingAuthority(application.licensing_authority ?? "");
    setLegalForm(application.business_legal_form ?? "llc");
    setIncorporationDate(application.incorporation_date ?? "");
    setBizAddress(application.business_address ?? "");
    setBizCity(application.business_city ?? "");
    setBizPostal(application.business_postal_code ?? "");
    setBizCountry(application.business_country ?? "AE");
    setBizEmail(application.business_email ?? "");
    setBizPhone(application.business_phone ?? "");
    setBizWebsite(application.business_website ?? "");
    setOwnerName(application.beneficial_owner_name ?? "");
    setVatNumber(application.vat_number ?? "");
    setTaxId(application.tax_id ?? "");
    setAcceptTerms(Boolean(application.terms_accepted_at));
  }, [application]);


  const { data: myDocs } = useQuery({
    queryKey: ["mp-kyc-docs", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data } = await supabase
        .from("marketplace_kyc_documents")
        .select("id, document_type, status, created_at")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error(t("mpsell.sell.error.signInRequired"));
      if (!legalName.trim() || !phone.trim() || !addressLine1.trim() || !city.trim() || !postalCode.trim()) {
        throw new Error(t("mpsell.sell.error.requiredFields"));
      }
      const isBiz = sellerType === "business";
      if (isBiz) {
        if (
          !businessName.trim() ||
          !regNumber.trim() ||
          !tradeLicense.trim() ||
          !tradeLicenseExpiry ||
          !licensingAuthority.trim() ||
          !bizAddress.trim() ||
          !bizCity.trim() ||
          !bizEmail.trim() ||
          !bizPhone.trim() ||
          !ownerName.trim()
        ) {
          throw new Error(t("mpsell.sell.error.businessFields"));
        }
      }
      if (files.length === 0 && (myDocs?.length ?? 0) === 0) {
        throw new Error(
          isBiz ? t("mpsell.sell.error.uploadBiz") : t("mpsell.sell.error.uploadIndividual"),
        );
      }
      const payload = {
        user_id: user.id,
        seller_type: sellerType,
        legal_name: legalName.trim(),
        country,
        residence_country: country,
        phone: phone.trim(),
        address_line1: addressLine1.trim(),
        address_city: city.trim(),
        address_postal_code: postalCode.trim(),
        business_name: isBiz ? businessName.trim() : null,
        business_registration_number: isBiz ? regNumber.trim() : null,
        trade_license_number: isBiz ? tradeLicense.trim() : null,
        trade_license_expiry: isBiz && tradeLicenseExpiry ? tradeLicenseExpiry : null,
        licensing_authority: isBiz ? licensingAuthority.trim() : null,
        business_legal_form: isBiz ? legalForm : null,
        incorporation_date: isBiz && incorporationDate ? incorporationDate : null,
        business_address: isBiz ? bizAddress.trim() : null,
        business_city: isBiz ? bizCity.trim() : null,
        business_postal_code: isBiz ? bizPostal.trim() || null : null,
        business_country: isBiz ? bizCountry : null,
        business_email: isBiz ? bizEmail.trim() : null,
        business_phone: isBiz ? bizPhone.trim() : null,
        business_website: isBiz ? bizWebsite.trim() || null : null,
        beneficial_owner_name: isBiz ? ownerName.trim() : null,
        vat_number: vatNumber.trim() || null,
        tax_id: taxId || null,

        application_status: "kyc_pending" as const,
        more_info_message: null,
        terms_version: "seller-terms-v1",
        terms_accepted_at: new Date().toISOString(),
        submitted_at: new Date().toISOString(),
      };
      const { data: app, error } = await supabase
        .from("marketplace_seller_applications")
        .upsert(payload, { onConflict: "user_id" })
        .select("id")
        .single();
      if (error) throw error;

      const { data: kycCase, error: caseErr } = await supabase
        .from("marketplace_kyc_cases")
        .insert({
          seller_application_id: app.id,
          user_id: user.id,
          kyc_mode: "manual",
          status: "submitted",
          due_at: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        })
        .select("id")
        .single();
      if (caseErr) throw caseErr;

      for (const file of files) {
        if (file.size > 3 * 1024 * 1024) throw new Error(t("mpsell.sell.error.fileTooLarge"));
        const ext = (file.name.split(".").pop() ?? "pdf").toLowerCase().slice(0, 5);
        const path = `${user.id}/kyc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("media").upload(path, file, { upsert: false });
        if (upErr) throw upErr;
        const { error: docErr } = await supabase.from("marketplace_kyc_documents").insert({
          kyc_case_id: kycCase.id,
          user_id: user.id,
          document_type: documentType,
          document_country: country,
          storage_reference: path,
          status: "submitted",
        });
        if (docErr) throw docErr;
      }
    },
    onSuccess: () => {
      setFiles([]);
      toast.success(t("mpsell.sell.toast.submitSuccess"));
      void qc.invalidateQueries({ queryKey: ["mp-seller-application", user?.id] });
      void qc.invalidateQueries({ queryKey: ["mp-kyc-docs", user?.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("mpsell.sell.toast.submitError")),
  });

  if (!user) return null;

  const status = application?.application_status ?? "not_a_seller";
  const STATUS_LABEL_TEXT: Record<string, string> = {
    application_draft: t("mpsell.sell.status.draft"),
    kyc_pending: t("mpsell.sell.status.kycPending"),
    kyc_more_info_required: t("mpsell.sell.status.moreInfo"),
    kyc_rejected: t("mpsell.sell.status.rejected"),
    seller_approved: t("mpsell.sell.status.approved"),
    seller_suspended: t("mpsell.sell.status.suspended"),
  };
  const statusMeta = STATUS_LABEL_TEXT[status] ? { label: STATUS_LABEL_TEXT[status], tone: STATUS_TONE[status] } : undefined;
  const canEdit =
    status === "not_a_seller" ||
    status === "application_draft" ||
    status === "kyc_rejected" ||
    status === "kyc_more_info_required";

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold">{t("mpcopy.sell.title")}</h1>

        {status === "seller_approved" ? (
          <div className="mt-6 space-y-6">
            <div className="card-surface flex items-center gap-3 p-5">
              <BadgeCheck className="size-6 text-primary" />
              <div>
                <p className="font-semibold">{t("mpsell.sell.approvedTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("mpsell.sell.approvedBody")}</p>
              </div>
              <Button asChild variant="hot" className="ml-auto">
                <Link to="/marketplace/services/new">
                  <Plus className="mr-1 size-4" /> {t("mpsell.sell.newService")}
                </Link>
              </Button>
            </div>

            <section>
              <h2 className="font-display text-lg font-bold">{t("mpsell.sell.yourServices")}</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("mpsell.sell.editHelper")}
              </p>
              {myServices?.length ? (
                <ul className="mt-3 space-y-2">
                  {myServices.map((s) => (
                    <li key={s.id} className="card-surface space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">{s.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {t("mpsell.sell.ordersRating", { count: s.orders_count, rating: Number(s.rating_avg).toFixed(1) })}
                          </p>
                        </div>
                        <Badge variant="outline" className="capitalize">
                          {s.status.replaceAll("_", " ")}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button asChild variant="outline" size="sm">
                          <Link to="/marketplace/services/$serviceId/edit" params={{ serviceId: s.id }}>
                            <Pencil className="size-4" /> {t("mpsell.sell.edit")}
                          </Link>
                        </Button>
                        {s.status === "active" || s.status === "approved" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setServiceStatus.mutate({ id: s.id, next: "paused" })}
                          >
                            <PauseCircle className="size-4" /> {t("mpsell.sell.pauseSelling")}
                          </Button>
                        ) : s.status === "paused" || s.status === "draft" || s.status === "changes_required" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setServiceStatus.mutate({ id: s.id, next: "pending_moderation" })}
                          >
                            <PlayCircle className="size-4" /> {t("mpsell.sell.submitForReview")}
                          </Button>
                        ) : null}
                        {(s.status === "active" || s.status === "approved") && (
                          <Button variant="hot" size="sm" onClick={() => setBoardService(s)}>
                            <Flame className="size-4" /> {s.listing_id ? t("mpsell.sell.boostOnBoards") : t("mpsell.sell.listOnBoards")}
                          </Button>
                        )}
                        {(s.status === "active" || s.status === "approved") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setFeatureService({
                                id: s.id,
                                title: s.title,
                                featured_until: s.featured_until ?? null,
                              })
                            }
                          >
                            <Sparkles className="size-4" />
                            {isFeatured(s.featured_until) ? t("mpsell.sell.extendFeaturing") : t("mpsell.sell.featureFrom")}
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">{t("mpsell.sell.noServicesYet")}</p>
              )}
            </section>

            <ServicePromoteDialog
              service={featureService}
              open={Boolean(featureService)}
              onOpenChange={(o) => {
                if (!o) setFeatureService(null);
              }}
            />

            <ServiceBoardDialog
              service={boardService}
              sellerUserId={user.id}
              sellerHandle={myProfile?.slug ?? myProfile?.handle ?? null}
              open={Boolean(boardService)}
              onOpenChange={(o) => {
                if (!o) setBoardService(null);
              }}
            />



          </div>
        ) : status !== "not_a_seller" && status !== "application_draft" ? (
          <div className="card-surface mt-6 p-6">
            <div className="flex items-center gap-3">
              <Clock className="size-6 text-primary" />
              <div>
                <p className="font-semibold">{t("mpsell.sell.applicationStatus")}</p>
                <Badge variant={statusMeta?.tone ?? "outline"} className="mt-1">
                  {statusMeta?.label ?? status}
                </Badge>
              </div>
            </div>
            {status === "kyc_more_info_required" && (
              <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
                <p className="text-sm font-semibold">{t("mpsell.sell.moreInfoTitle")}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {application?.more_info_message ??
                    t("mpsell.sell.moreInfoDefault")}
                </p>
              </div>
            )}
            {status === "kyc_rejected" && application?.rejection_reason && (
              <p className="mt-4 text-sm text-muted-foreground">{t("mpsell.sell.rejectionReason", { reason: application.rejection_reason })}</p>
            )}
          </div>
        ) : null}

        {canEdit && (
          <form
            className="card-surface mt-6 space-y-5 p-6"
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
          >
            <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-4">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold">{t("mpcopy.sell.verify_title")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("mpcopy.sell.verify_body")}</p>
              </div>
            </div>

            <div>
              <Label>{t("mpsell.sell.sellerType")}</Label>
              <RadioGroup
                className="mt-2 flex gap-4"
                value={sellerType}
                onValueChange={(v) => setSellerType(v as "individual" | "business")}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="individual" id="st-ind" />
                  <Label htmlFor="st-ind">{t("mpsell.sell.individual")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="business" id="st-biz" />
                  <Label htmlFor="st-biz">{t("mpsell.sell.business")}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="legal-name">
                  {sellerType === "business" ? t("mpsell.sell.legalNameBusiness") : t("mpsell.sell.legalNameIndividual")} *
                </Label>
                <Input id="legal-name" value={legalName} onChange={(e) => setLegalName(e.target.value)} required maxLength={120} />
              </div>
              <div>
                <Label htmlFor="phone">{t("mpsell.sell.phoneNumber")}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+971 50 000 0000"
                  required
                  maxLength={32}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="addr1">{t("mpsell.sell.address")}</Label>
                <Input id="addr1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required maxLength={160} />
              </div>
              <div>
                <Label htmlFor="city">{t("mpsell.sell.city")}</Label>
                <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} required maxLength={80} />
              </div>
              <div>
                <Label htmlFor="postal">{t("mpsell.sell.postalCode")}</Label>
                <Input id="postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required maxLength={20} />
              </div>
              <div>
                <Label>{t("mpsell.sell.country")}</Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {sellerType === "business" && (
              <div className="space-y-4 rounded-lg border border-border p-4">
                <p className="text-sm font-semibold">{t("mpsell.sell.businessProfile")}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="biz-name">{t("mpsell.sell.registeredCompanyName")}</Label>
                    <Input id="biz-name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required maxLength={160} />
                  </div>
                  <div>
                    <Label>{t("mpsell.sell.legalForm")}</Label>
                    <Select value={legalForm} onValueChange={setLegalForm}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="llc">{t("mpsell.sell.legalForm.llc")}</SelectItem>
                        <SelectItem value="fzco">{t("mpsell.sell.legalForm.fzco")}</SelectItem>
                        <SelectItem value="sole_establishment">{t("mpsell.sell.legalForm.sole")}</SelectItem>
                        <SelectItem value="branch">{t("mpsell.sell.legalForm.branch")}</SelectItem>
                        <SelectItem value="partnership">{t("mpsell.sell.legalForm.partnership")}</SelectItem>
                        <SelectItem value="corporation">{t("mpsell.sell.legalForm.corporation")}</SelectItem>
                        <SelectItem value="other">{t("mpsell.sell.legalForm.other")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="reg-num">{t("mpsell.sell.registrationNumber")}</Label>
                    <Input id="reg-num" value={regNumber} onChange={(e) => setRegNumber(e.target.value)} required maxLength={64} />
                  </div>
                  <div>
                    <Label htmlFor="trade-lic">{t("mpsell.sell.tradeLicenceNumber")}</Label>
                    <Input id="trade-lic" value={tradeLicense} onChange={(e) => setTradeLicense(e.target.value)} required maxLength={64} />
                  </div>
                  <div>
                    <Label htmlFor="trade-exp">{t("mpsell.sell.tradeLicenceExpiry")}</Label>
                    <Input id="trade-exp" type="date" value={tradeLicenseExpiry} onChange={(e) => setTradeLicenseExpiry(e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="lic-auth">{t("mpsell.sell.licensingAuthority")}</Label>
                    <Input id="lic-auth" value={licensingAuthority} onChange={(e) => setLicensingAuthority(e.target.value)} placeholder={t("mpsell.sell.licensingAuthorityPlaceholder")} required maxLength={120} />
                  </div>
                  <div>
                    <Label htmlFor="inc-date">{t("mpsell.sell.incorporationDate")}</Label>
                    <Input id="inc-date" type="date" value={incorporationDate} onChange={(e) => setIncorporationDate(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="owner">{t("mpsell.sell.beneficialOwner")}</Label>
                    <Input id="owner" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} required maxLength={120} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="biz-addr">{t("mpsell.sell.registeredBusinessAddress")}</Label>
                    <Input id="biz-addr" value={bizAddress} onChange={(e) => setBizAddress(e.target.value)} required maxLength={200} />
                  </div>
                  <div>
                    <Label htmlFor="biz-city">{t("mpsell.sell.businessCity")}</Label>
                    <Input id="biz-city" value={bizCity} onChange={(e) => setBizCity(e.target.value)} required maxLength={80} />
                  </div>
                  <div>
                    <Label htmlFor="biz-postal">{t("mpsell.sell.businessPostalCode")}</Label>
                    <Input id="biz-postal" value={bizPostal} onChange={(e) => setBizPostal(e.target.value)} maxLength={20} />
                  </div>
                  <div>
                    <Label>{t("mpsell.sell.businessCountry")}</Label>
                    <Select value={bizCountry} onValueChange={setBizCountry}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="biz-email">{t("mpsell.sell.businessEmail")}</Label>
                    <Input id="biz-email" type="email" value={bizEmail} onChange={(e) => setBizEmail(e.target.value)} required maxLength={160} />
                  </div>
                  <div>
                    <Label htmlFor="biz-phone">{t("mpsell.sell.businessPhone")}</Label>
                    <Input id="biz-phone" type="tel" value={bizPhone} onChange={(e) => setBizPhone(e.target.value)} required maxLength={32} />
                  </div>
                  <div>
                    <Label htmlFor="biz-web">{t("mpsell.sell.website")}</Label>
                    <Input id="biz-web" value={bizWebsite} onChange={(e) => setBizWebsite(e.target.value)} placeholder="https://" maxLength={200} />
                  </div>
                  <div>
                    <Label htmlFor="vat-num">{t("mpsell.sell.vatTrn")}</Label>
                    <Input id="vat-num" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} maxLength={64} />
                  </div>
                </div>
              </div>
            )}


            <div>
              <Label htmlFor="tax-id">{t("mpsell.sell.taxIdLabel")}</Label>
              <Input id="tax-id" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
            </div>

            <div className="rounded-lg border border-dashed border-border p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Upload className="size-4" /> {t("mpsell.sell.identityDocuments")}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {sellerType === "business"
                  ? t("mpsell.sell.identityBizHint")
                  : t("mpsell.sell.identityIndividualHint")}{" "}
                {t("mpsell.sell.fileSizeHint")}
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>{t("mpsell.sell.documentType")}</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="passport">{t("mpsell.sell.docType.passport")}</SelectItem>
                      <SelectItem value="national_id">{t("mpsell.sell.docType.nationalId")}</SelectItem>
                      <SelectItem value="driving_licence">{t("mpsell.sell.docType.drivingLicence")}</SelectItem>
                      <SelectItem value="proof_of_address">{t("mpsell.sell.docType.proofOfAddress")}</SelectItem>
                      <SelectItem value="company_registration">{t("mpsell.sell.docType.companyRegistration")}</SelectItem>
                      <SelectItem value="trade_licence">{t("mpsell.sell.docType.tradeLicence")}</SelectItem>
                      <SelectItem value="memorandum">{t("mpsell.sell.docType.memorandum")}</SelectItem>
                      <SelectItem value="ownership_structure">{t("mpsell.sell.docType.ownershipStructure")}</SelectItem>
                      <SelectItem value="vat_certificate">{t("mpsell.sell.docType.vatCertificate")}</SelectItem>
                      <SelectItem value="bank_letter">{t("mpsell.sell.docType.bankLetter")}</SelectItem>
                      <SelectItem value="board_resolution">{t("mpsell.sell.docType.boardResolution")}</SelectItem>

                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="kyc-files">{t("mpsell.sell.uploadFiles")}</Label>
                  <Input
                    id="kyc-files"
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={(e) => {
                      const selectedFiles = Array.from(e.target.files ?? []);
                      const oversizedFile = selectedFiles.find((file) => file.size > 3 * 1024 * 1024);
                      if (oversizedFile) {
                        toast.error(t("mpsell.sell.toast.oversizedFile", { name: oversizedFile.name }));
                        e.target.value = "";
                        setFiles([]);
                        return;
                      }
                      setFiles(selectedFiles);
                    }}
                  />
                </div>
              </div>
              {files.length > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("mpsell.sell.filesReady", { count: files.length })}
                </p>
              )}
              {myDocs && myDocs.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                  {myDocs.map((d) => (
                    <li key={d.id}>
                      {t("mpsell.sell.docLine", {
                        type: d.document_type.replaceAll("_", " "),
                        status: d.status,
                        date: new Date(d.created_at).toLocaleDateString(),
                      })}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="seller-terms"
                checked={acceptTerms}
                onCheckedChange={(v) => setAcceptTerms(v === true)}
              />
              <Label htmlFor="seller-terms" className="text-xs font-normal leading-relaxed text-muted-foreground">
                {t("mpsell.sell.acceptTermsPrefix")}{" "}
                <Link to="/terms" className="underline">{t("mpsell.sell.acceptTermsLink")}</Link>{" "}
                {t("mpsell.sell.acceptTermsSuffix")}
              </Label>
            </div>

            <Button
              type="submit"
              variant="hot"
              disabled={!acceptTerms || !legalName || !phone || !addressLine1 || submit.isPending}
            >
              <FileText className="mr-2 size-4" />
              {submit.isPending
                ? t("mpsell.sell.submitting")
                : status === "kyc_more_info_required"
                  ? t("mpsell.sell.resubmitApplication")
                  : t("mpsell.sell.submitApplication")}
            </Button>
          </form>
        )}
      </div>
    </MarketplaceGate>
  );
}

